import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import config from '../../../constants/apiConfig';

type IncidentDivision = {
  id?: UUID;
  incident_id: UUID;
  name?: string;
  division_supervisor_name?: string;
  section?: string;
};

export const keys = {
  base: 'divisions',
  get all() {
    return [keys.base];
  },
  get: function (group_guid: UUID, user_guid: UUID) {
    return [keys.base, group_guid, user_guid];
  },
} as const;

export const useIncidentDivisions = (incidentId: string) => {
  return useQuery({
    queryKey: ['divisions', incidentId],
    queryFn: async () => {
      const resp = await axios.get<IncidentDivision[]>(
        `${config.apiGateway.ics_incident_divisions}/${incidentId}`
      );
      return resp.data;
    },
    enabled: !!incidentId,
    throwOnError: () => {
      return false;
    },
  });
};

export const useCreateIncidentDivision = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (newIncidentDivision: IncidentDivision) => {
      const resp = await axios.post<IncidentDivision>(
        `${config.apiGateway.ics_incident_divisions}`,
        newIncidentDivision
      );
      return resp.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.all });
    },
  });
};

export const useUpdateIncidentDivision = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (updatedIncidentDivision: IncidentDivision) => {
      const resp = await axios.put<IncidentDivision>(
        `${config.apiGateway.ics_incident_divisions}/${updatedIncidentDivision.id}`,
        updatedIncidentDivision
      );
      return resp.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.all });
    },
  });
};

export const useDeleteIncidentDivision = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: UUID) => {
      await axios.delete(`${config.apiGateway.ics_incident_divisions}/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: keys.all });
    },
  });
};
